package dev.kuml.portal.web.editor

import org.w3c.dom.Element
import org.w3c.dom.parsing.DOMParser

/**
 * The DOM-touching half of R-27's SVG inlining (`DOMParser` + tree-walk) -- the pure allow/deny
 * decisions themselves live in [SvgSanitizePolicy], kept separate so those decisions stay directly
 * unit-testable without a browser DOM (see that object's class KDoc for the full XSS-surface
 * rationale this exists to close).
 *
 * [inline] is the ONLY entry point `EditorScreen.kt`'s `showDiagram` needs: it returns a sanitized,
 * ready-to-`appendChild` root `<svg>` [Element] (every `id`, model-element and renderer-internal
 * structural group alike, uniformly rewritten to `data-kuml-id` -- `DiagramCanvas.transitionFor`
 * validates a clicked id against `/outline`'s [OutlineElement.id]s before ever treating it as a
 * selection, see that function's own KDoc for why the id rewrite itself no longer special-cases
 * anything), or `null` if inlining is not safe/possible for this particular payload. A `null` result
 * is never an error the caller needs to surface: [EditorScreen.kt] falls back to the pre-R-27
 * `<img src="blob:...">` display for that one diagram load, which is strictly SAFER (an `<img>`
 * never executes script/event-handler content) just non-interactive -- selection/inspector editing
 * is simply unavailable for that render, exactly as it always was before this wave existed.
 */
public object SvgInliner {
    /**
     * `null` for:
     *  - [SvgSanitizePolicy.needsIdPrefixing] returning `true` -- the K-4 fail-safe trigger. This
     *    object deliberately does NOT implement the "prefix every id and rewrite every `url(#...)`
     *    reference" fallback [SvgSanitizePolicy]'s KDoc describes as the alternative scheme: no
     *    real kUML render exercises that path today (verified against v0.54.1's actual renderer,
     *    see that KDoc), and a half-tested id-prefixing/reference-rewriting implementation would
     *    itself be a correctness risk for a path this codebase cannot currently exercise even in a
     *    test. Falling back to the pre-R-27 `<img>` display is the strictly safer choice until a
     *    real render actually needs the richer scheme.
     *  - a malformed payload the browser's own `DOMParser` cannot parse as XML (surfaced as a
     *    `<parsererror>` document -- browsers never throw for this, they document that error
     *    inline instead) or whose root element is not itself an allowed tag.
     */
    public fun inline(rawSvg: String): Element? {
        if (SvgSanitizePolicy.needsIdPrefixing(rawSvg)) return null
        val document = DOMParser().parseFromString(rawSvg, "image/svg+xml")
        if (document.getElementsByTagName("parsererror").length > 0) return null
        val root = document.documentElement ?: return null
        if (!isAllowedElement(root)) return null
        sanitize(root)
        return root
    }

    /**
     * `true` iff [element] may appear in the sanitized tree at all -- its TAG is
     * [SvgSanitizePolicy.isAllowedTag] AND, for a `<style>` element specifically, its TEXT content is
     * also [SvgSanitizePolicy.isSafeStyleContent] (see that function's KDoc for why `<style>` alone
     * among allowed tags needs its content, not just its tag/attributes, inspected). Shared by [inline]
     * (the root element, which [sanitize] never revisits) and [sanitize] (every non-root child) so the
     * two call sites cannot drift apart the way they previously did -- a `<style>` root element used to
     * skip the content check entirely because only [sanitize]'s child loop applied it.
     */
    private fun isAllowedElement(element: Element): Boolean =
        SvgSanitizePolicy.isAllowedTag(element.tagName) &&
            (element.tagName.lowercase() != "style" || SvgSanitizePolicy.isSafeStyleContent(element.textContent ?: ""))

    /**
     * Recursive tree-walk: every attribute [SvgSanitizePolicy.isAllowedAttribute] rejects is
     * stripped, every `id` is rewritten to `data-kuml-id` (K-4's "safe flat id-rewrite scheme" --
     * only reached at all when [inline] already confirmed [SvgSanitizePolicy.needsIdPrefixing] is
     * `false`), and every child [SvgSanitizePolicy.isAllowedTag] rejects is removed outright,
     * children included -- an allow-list failing closed, per that object's own KDoc.
     *
     * Both `element.attributes`/`element.childNodes` are LIVE [org.w3c.dom.NamedNodeMap]/
     * [org.w3c.dom.NodeList] views -- removing an attribute/child while iterating the live
     * collection itself would skip entries (the collection shrinks under the iterator), so both
     * loops below snapshot the names/child elements into a plain Kotlin `List` FIRST.
     */
    private fun sanitize(element: Element) {
        val attributeNames = (0 until element.attributes.length).mapNotNull { element.attributes.item(it)?.name }
        for (name in attributeNames) {
            val value = element.getAttribute(name) ?: continue
            if (!SvgSanitizePolicy.isAllowedAttribute(name, value)) {
                element.removeAttribute(name)
            }
        }

        val id = element.getAttribute("id")
        if (id != null) {
            element.removeAttribute("id")
            // Every id is rewritten uniformly, renderer-internal structural group ids (`"nodes"`,
            // `"edges"`, `"kuml-diagram-frame"`) included (review fix, MINOR round 2, 2026-09-02 --
            // reverts the round-1 fix's selective stripping, see `DiagramCanvas.transitionFor`'s own
            // KDoc for why). Whether the resulting `data-kuml-id` can ever produce a selection is now
            // `DiagramCanvas.transitionFor`'s job -- it validates every clicked id against the
            // outline before treating it as anything -- not this sanitizer's: stripping the id here
            // instead made a real classifier legitimately named e.g. `"nodes"` permanently
            // unclickable, which validating against the outline does not.
            element.setAttribute("data-kuml-id", id)
        }

        val children = (0 until element.childNodes.length).mapNotNull { element.childNodes.item(it) as? Element }
        for (child in children) {
            // HARDENING (security-review finding, 2026-09-02, see SvgSanitizePolicy.isSafeStyleContent's
            // KDoc): `<style>`'s TAG/ATTRIBUTEs alone being allowed says nothing about its TEXT content,
            // which is what actually reaches the page as CSS -- unlike every other allowed tag, this one
            // carries its dangerous payload as text, not as an attribute or a child element. Not
            // reachable today (verified in that KDoc), but a `<style>` whose content this policy does not
            // recognise is dropped outright rather than trusted, exactly like an unrecognised TAG would
            // be. [isAllowedElement] applies this same check to the root element in [inline].
            if (isAllowedElement(child)) {
                sanitize(child)
            } else {
                element.removeChild(child)
            }
        }
    }
}
